import { useGlobalStore } from "@/GlobalStore";
import { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import {
  fetchAccountBrands,
  getBrandStatus,
  fetchMultiBrandAds,
  removeAdFromFolder,
  saveAdToFolder,
  generateAd,
  fetchBrandConfig,
  addDemoAccount,
} from "../../../apiService";
import { AccountBrand, BrandAd, BrandConfig } from "../../../types";
import { syncBrandsWithExtension, needsSync } from "@/utils/brandSync";
import { LuTriangleAlert, LuInfo } from "react-icons/lu";
import ReportTitle from "../../report/ReportTitle";
import { Separator } from "@/components/ui/separator";
import { useCommentAnalysisStore } from "@/screens/analysis/store";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { ClickableToast } from "@/components/common/toast/ClickableToast";
import { MasonryLayout } from "@/components/common/MasonryLayout";
import { Button } from "@/components/ui/button";
import MediaTypeDropdown from "@/screens/creatives/components/filters/MediaTypeDropdown";
import AdCard from "@/components/common/adCard";
import BookmarkAd from "../../adActions/BookmarkAd";
import CreateAd, { CreateAdSettings } from "../../adActions/CreateAd";
import SelectAd from "../../adActions/SelectAd";
import DownloadAction from "@/components/common/adCard/actions/DownloadAction";
import CreativeInputSection from "@/screens/creative-library/components/common/CreativeInputSection";
import { AspectRatio } from "@/screens/creative-library/components/tabs/AspectRatioDropdown";
import { Variations } from "@/screens/creative-library/components/tabs/VariationsDropdown";
import {
  coerceModel,
  DEFAULT_MODEL,
  Model,
} from "@/screens/creative-library/components/tabs/ModelDropdown";
import { File as CustomFile, LibraryMedia } from "@/types";
import { UploadingFile } from "@/components/hoc/fileAttachment/types";
import { uploadFilesAsync } from "@/api/globalApiService";
import AdStatusDropdown from "../filters/AdStatusDropdown";
import axios, { CancelTokenSource } from "axios";
import SortByDropdown from "../filters/SortByDropdown";
import CompetitorsDropdown from "../filters/CompetitorsDropdown";
import LocationDropdown, { LocationCode } from "../filters/LocationDropdown";
import NoAds from "../emptyState/NoAds";
import BrandFilterChips from "./BrandFilterChips";
import { AddCompetitorDialog } from "./AddCompetitorDialog";
import PageLoader from "@/components/common/loaders/PageLoader";
import ItemLoader from "@/components/common/loaders/ItemLoader";
import { IconInfoCircle, IconRefresh } from "@tabler/icons-react";
import { RiTrophyLine } from "@remixicon/react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { buildRoute } from "@/router/constants";
import CompetitorAdsOverview from "./CompetitorAdsOverview";
import NoCompetitors from "@/components/common/emptyStates/NoCompetitors";
import { ReportDate, TimeInterval } from "@/types";
import { getPredefinedRanges, formatForAPI } from "@/utils/dateUtils";
import { useNavigate } from "react-router-dom";
import ToastCompact from "@/components/common/toast/ToastCompact";
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  CREATIVE_LIBRARY_STORAGE_KEYS,
} from "@/utils/localStorage";
import { Folder } from "@/screens/analysis/types";
import MoodboardManagerDialog from "@/screens/creative-library/components/dialog/MoodboardManagerDialog";
import { useMoodboardsManager } from "@/screens/creative-library/hooks/useMoodboardsManager";
import type { Moodboard } from "@/screens/creative-library/types";

// Memoized card component to prevent object recreation on every render
interface CompetitorAdCardProps {
  ad: BrandAd;
  isSelected: boolean;
  totalAttachments: number;
  attachmentLimit: number;
  folders: Folder[];
  rank: number | null;
  onAdToggle: (adId: string) => void;
  onBookmark: (adId: string, folderId: string) => Promise<void>;
  onCreateAd: (adId: string, settings: CreateAdSettings) => Promise<void>;
  creativeModel: Model;
  creativeAspectRatio: AspectRatio;
  creativeVariations: Variations;
  creativeDefaultPromptId: string | null;
  moodboards: Moodboard[];
  moodboardsLoading: boolean;
  selectedMoodboardId: string | null;
  onCreateMoodboard: () => void;
  onEditMoodboard: (moodboardId: string) => void;
  onSettingsChange: (settings: {
    model: Model;
    aspectRatio: AspectRatio;
    variations: Variations;
    defaultPromptId: string | null;
    moodboardId: string | null;
  }) => void;
}

const CompetitorAdCard = memo(
  ({
    ad,
    isSelected,
    totalAttachments,
    attachmentLimit,
    rank,
    onAdToggle,
    onBookmark,
    onCreateAd,
    creativeModel,
    creativeAspectRatio,
    creativeVariations,
    creativeDefaultPromptId,
    moodboards,
    moodboardsLoading,
    selectedMoodboardId,
    onCreateMoodboard,
    onEditMoodboard,
    onSettingsChange,
  }: CompetitorAdCardProps) => {
    // Memoize the header object
    const rankDisplay = rank != null ? (rank > 99 ? "99+" : `${rank}`) : null;

    const header = useMemo(
      () => ({
        title: ad.creative.account_name,
        icon: ad.creative.account_icon_url || "",
        meta: (
          <div className="flex items-center gap-4 pr-[6px]">
            {rankDisplay && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-brand-primary-new text-[12px] font-normal leading-[18px] tracking-[0.24px] flex items-center gap-[6px] cursor-default">
                      <RiTrophyLine size={14} color="#9AA3BD" className="shrink-0" />
                      <span className="pt-[2px]">{rankDisplay}</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start">
                    <p>{rank != null && rank > 99 ? `Impression rank: ${rank}` : "Impression rank"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-auto flex items-center gap-[6px] cursor-default">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        ad.status === "active" ? "bg-[#039F54]" : "bg-[#E7E8E9]"
                      }`}
                    ></span>
                    <span className="text-brand-primary-new text-right text-[12px] font-normal leading-[18px] tracking-[0.24px] relative top-[1px]">
                      {ad.running_days}D
                    </span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end">
                  <p>Active days</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
      }),
      [
        ad.creative.account_name,
        ad.creative.account_icon_url,
        ad.status,
        ad.running_days,
        rank,
        rankDisplay,
      ],
    );

    // Memoize the actions array
    const actions = useMemo(() => {
      const actionsArr = [];

      // Select action - top left (only for images)
      if (ad.creative.creative_type === "image") {
        actionsArr.push({
          component: (
            <SelectAd
              isSelected={isSelected}
              onToggle={() => onAdToggle(ad.id)}
              disabled={!isSelected && totalAttachments >= attachmentLimit}
            />
          ),
          position: "top-left" as const,
          visibility: isSelected ? ("always" as const) : ("hover" as const),
        });
      }

      // Download action
      actionsArr.push({
        component: (
          <DownloadAction
            url={
              ad.creative.creative_type === "video"
                ? ad.creative?.videos?.[0]?.permalink_url
                : ad.creative?.images?.[0]?.uncompressed_url ||
                  ad.creative?.images?.[0]?.permalink_url
            }
            type={ad.creative.creative_type}
            accountName={ad.creative.account_name}
          />
        ),
        position: "top-right" as const,
        visibility: "hover" as const,
      });

      // Bookmark action
      actionsArr.push({
        component: (
          <BookmarkAd
            ad={ad}
            onBookmark={(folderId) => {
              void onBookmark(ad.id, folderId);
            }}
          />
        ),
        position: "top-right" as const,
        visibility: ad.folders.length > 0 ? ("always" as const) : ("hover" as const),
      });

      // Reimagine action - only for images and when not selected
      if (ad.creative.creative_type === "image" && !isSelected) {
        actionsArr.push({
          component: (
            <CreateAd
              ad={ad}
              onCreateAd={(settings) => onCreateAd(ad.id, settings)}
              onSelectAd={() => onAdToggle(ad.id)}
              currentModel={creativeModel}
              currentAspectRatio={creativeAspectRatio}
              currentVariations={creativeVariations}
              currentDefaultPromptId={creativeDefaultPromptId}
              currentMoodboardId={selectedMoodboardId}
              moodboards={moodboards}
              moodboardsLoading={moodboardsLoading}
              onCreateMoodboard={onCreateMoodboard}
              onEditMoodboard={onEditMoodboard}
              onSettingsChange={onSettingsChange}
            />
          ),
          position: "bottom-left" as const,
          visibility: "hover" as const,
        });
      }

      return actionsArr;
    }, [
      ad,
      isSelected,
      totalAttachments,
      attachmentLimit,
      onAdToggle,
      onBookmark,
      onCreateAd,
      creativeModel,
      creativeAspectRatio,
      creativeVariations,
      creativeDefaultPromptId,
      moodboards,
      moodboardsLoading,
      selectedMoodboardId,
      onCreateMoodboard,
      onEditMoodboard,
      onSettingsChange,
    ]);

    // Memoize the body object
    const body = useMemo(
      () => ({
        media: {
          ad_id: ad.id,
          source: ad.creative?.videos?.[0]
            ? ad.creative?.videos?.[0]?.permalink_url
            : ad.creative?.images?.[0]?.permalink_url,
          type: ad.creative?.videos?.[0] ? ("video" as const) : ("image" as const),
          thumbnail: ad.creative.thumbnail_url,
          crossOrigin: "anonymous" as const,
          isNew: ad.isNew,
          actions,
        },
      }),
      [ad.creative, ad.isNew, actions],
    );

    return <AdCard header={header} body={body} />;
  },
  // Custom comparison function for memo
  (prevProps, nextProps) => {
    return (
      prevProps.ad.id === nextProps.ad.id &&
      prevProps.ad.status === nextProps.ad.status &&
      prevProps.ad.isNew === nextProps.ad.isNew &&
      prevProps.ad.folders.length === nextProps.ad.folders.length &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.totalAttachments === nextProps.totalAttachments &&
      prevProps.rank === nextProps.rank &&
      prevProps.creativeModel === nextProps.creativeModel &&
      prevProps.creativeAspectRatio === nextProps.creativeAspectRatio &&
      prevProps.creativeVariations === nextProps.creativeVariations &&
      prevProps.creativeDefaultPromptId === nextProps.creativeDefaultPromptId &&
      prevProps.selectedMoodboardId === nextProps.selectedMoodboardId &&
      prevProps.moodboardsLoading === nextProps.moodboardsLoading &&
      prevProps.moodboards === nextProps.moodboards
    );
  },
);
CompetitorAdCard.displayName = "CompetitorAdCard";

const CompetitorAdsAnalysis = () => {
  const { selectedAccount, sidebarCollapsed } = useGlobalStore();
  const { selectedSidebarItem, folders } = useCommentAnalysisStore();
  const navigate = useNavigate();
  const [brands, setBrands] = useState<AccountBrand[] | null>(null);
  const [brandConfig, setBrandConfig] = useState<BrandConfig | null>(null);
  const [allAds, setAllAds] = useState<BrandAd[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [adsLoading, setAdsLoading] = useState(false);
  const [brandConfigLoading, setBrandConfigLoading] = useState(true);
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [newAds, setNewAds] = useState<BrandAd[]>([]);
  const [isAddCompetitorDialogOpen, setIsAddCompetitorDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<ReportDate | null>(null);

  // Multi-select state for ads
  const [selectedAdIds, setSelectedAdIds] = useState<string[]>([]);

  // Creative input section state - initialize from localStorage (same as CreateDashboard)
  const [creativePrompt, setCreativePrompt] = useState("");
  const [creativeAspectRatio, setCreativeAspectRatio] = useState<AspectRatio>(
    () =>
      (loadFromLocalStorage(
        CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO,
        "Auto",
      ) as AspectRatio) || "Auto",
  );
  const [creativeVariations, setCreativeVariations] = useState<Variations>(
    () =>
      (loadFromLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, 4) as Variations) ||
      4,
  );
  const [creativeModel, setCreativeModel] = useState<Model>(() =>
    coerceModel(loadFromLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.MODEL, DEFAULT_MODEL)),
  );
  const [creativeAvoidDangerZone, setCreativeAvoidDangerZone] = useState(
    () =>
      (loadFromLocalStorage(
        CREATIVE_LIBRARY_STORAGE_KEYS.AVOID_DANGER_ZONE,
        false,
      ) as boolean) || false,
  );
  const [creativeDefaultPromptId, setCreativeDefaultPromptId] = useState<string | null>(
    () =>
      (loadFromLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.DEFAULT_PROMPT_ID, null) as
        | string
        | null) || null,
  );
  const [creativeFiles, setCreativeFiles] = useState<CustomFile[]>([]);
  const [creativeLibraryMedia, setCreativeLibraryMedia] = useState<LibraryMedia[]>([]);
  const [creativeUploadingFiles, setCreativeUploadingFiles] = useState<UploadingFile[]>(
    [],
  );
  const [isCreativeFileUploading, setIsCreativeFileUploading] = useState(false);
  const [isCreativeSubmitting, setIsCreativeSubmitting] = useState(false);
  const [creativePopoverOpen, setCreativePopoverOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const creativeFileInputRef = useRef<HTMLInputElement>(null);

  // Moodboards (Style) - shared hook (same behavior as CreateDashboard)
  const {
    moodboards,
    moodboardsLoading,
    selectedMoodboardId,
    setSelectedMoodboardId,
    moodboardDialogOpen,
    setMoodboardDialogOpen,
    moodboardDialogInitialActiveId,
    openMoodboardDialog,
    onMoodboardsChange,
  } = useMoodboardsManager({ accountId: selectedAccount?.id });

  // Persist "Style" selection per account (moodboards are account-scoped)
  const moodboardStorageKey = useMemo(
    () =>
      selectedAccount?.id
        ? `${CREATIVE_LIBRARY_STORAGE_KEYS.MOODBOARD_ID}:${selectedAccount.id}`
        : CREATIVE_LIBRARY_STORAGE_KEYS.MOODBOARD_ID,
    [selectedAccount?.id],
  );
  const didInitMoodboardFromStorageRef = useRef(false);
  useEffect(() => {
    // Re-init when account changes
    didInitMoodboardFromStorageRef.current = false;
  }, [selectedAccount?.id]);
  useEffect(() => {
    if (didInitMoodboardFromStorageRef.current) return;
    didInitMoodboardFromStorageRef.current = true;
    const stored = loadFromLocalStorage<string | null>(moodboardStorageKey, null);
    if (stored) setSelectedMoodboardId(stored);
  }, [moodboardStorageKey, setSelectedMoodboardId]);

  useEffect(() => {
    // Don't persist optimistic ids; those are local-only.
    if (!selectedMoodboardId) {
      saveToLocalStorage(moodboardStorageKey, null);
      return;
    }
    if (selectedMoodboardId.startsWith("optimistic-")) return;
    saveToLocalStorage(moodboardStorageKey, selectedMoodboardId);
  }, [moodboardStorageKey, selectedMoodboardId]);

  useEffect(() => {
    // If stored id doesn't exist for this account, fallback to None.
    if (moodboardsLoading) return;
    if (!selectedMoodboardId) return;
    if (selectedMoodboardId.startsWith("optimistic-")) return;
    const exists = moodboards.some((m) => m.id === selectedMoodboardId);
    if (exists) return;
    setSelectedMoodboardId(null);
    saveToLocalStorage(moodboardStorageKey, null);
  }, [
    moodboardStorageKey,
    moodboardsLoading,
    moodboards,
    selectedMoodboardId,
    setSelectedMoodboardId,
  ]);

  const handleQuickCreateMoodboard = useCallback(() => {
    if (!selectedAccount?.id) return;
    const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const now = new Date().toISOString();
    const optimistic = {
      id: tempId,
      name: "My Moodboard",
      created_at: now,
      updated_at: now,
      files: [],
    };
    // Add immediately + select it as Style + open modal focused on it
    onMoodboardsChange((prev) => [...prev, optimistic]);
    setSelectedMoodboardId(tempId);
    openMoodboardDialog(tempId);
  }, [
    onMoodboardsChange,
    openMoodboardDialog,
    selectedAccount?.id,
    setSelectedMoodboardId,
  ]);

  const ATTACHMENT_LIMIT = 5;

  const isMounted = useRef(true);
  const loadAdsCancelTokenRef = useRef<CancelTokenSource | null>(null);
  const loadAdsPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancel tokens and polling timeouts for brands loading
  const loadBrandsCancelTokenRef = useRef<CancelTokenSource | null>(null);
  const loadBrandsPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadBrandsRetryCountRef = useRef<number>(0);

  // Track pollBrandStatus timeouts to prevent memory leaks
  const pollBrandStatusTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Debounce timer for loading ads on brandConfig changes
  const loadAdsDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isClickOnSyncedRef = useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Pagination state
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Grid width for centering sync button
  const [gridWidth, setGridWidth] = useState(0);

  // Polling state
  const [totalCount, setTotalCount] = useState(0);
  const [previousTotalCount, setPreviousTotalCount] = useState(0);
  const [newAdsCursor, setNewAdsCursor] = useState<string | null>(null);
  const [newAdsHasNext, setNewAdsHasNext] = useState(false);

  const PAGE_SIZE = 30;

  // Retry configuration constants
  const MAX_RETRY_ATTEMPTS = 3;
  const BASE_RETRY_DELAY = 5000; // 5 seconds base delay
  const MAX_RETRY_DELAY = 30000; // 30 seconds max delay

  // Helper functions to extract values from brandConfig
  const getSelectedBrands = useCallback(() => {
    if (!brands || !brandConfig) return [];
    return brands.filter((brand) => brandConfig.brand_ids.includes(brand.id));
  }, [brands, brandConfig]);

  const getMediaType = (): "image" | "video" | null => {
    if (!brandConfig) return null;
    const filter = brandConfig.filters.find((f) => f.key === "creative.creative_type");
    if (!filter || filter.conditions.length === 0) return null;
    return filter.conditions[0]?.value as "image" | "video";
  };

  const getAdStatus = (): "active" | "inactive" | null => {
    if (!brandConfig) return null;
    const filter = brandConfig.filters.find((f) => f.key === "status");
    if (!filter || filter.conditions.length === 0) return null;
    return filter.conditions[0]?.value as "active" | "inactive";
  };

  const getLocation = useCallback((): LocationCode | null => {
    return brandConfig?.location ?? null;
  }, [brandConfig?.location]);

  const getSortBy = () => {
    if (!brandConfig) return "newest";
    return brandConfig.sort_type;
  };

  const selectedBrands = useMemo(() => getSelectedBrands(), [getSelectedBrands]);
  const mediaType = getMediaType();
  const adStatus = getAdStatus();
  const location = useMemo(() => getLocation(), [getLocation]);
  const sortBy = getSortBy();
  const isImpressionSortDisabled = selectedBrands.length > 1;
  const filters = useMemo(() => brandConfig?.filters || [], [brandConfig?.filters]);

  // Helper to update brandConfig using functional state to avoid stale closures
  const updateBrandConfig = (
    updates: Partial<BrandConfig> | ((prev: BrandConfig) => BrandConfig),
  ) => {
    setBrandConfig((prev) => {
      if (!prev) return prev;
      if (typeof updates === "function") {
        return (updates as (prev: BrandConfig) => BrandConfig)(prev);
      }
      return { ...prev, ...updates };
    });
  };

  const updateFilters = (key: string, value: string | null) => {
    if (!brandConfig) return;
    const newFilters = brandConfig.filters.filter((f) => f.key !== key);

    if (value) {
      newFilters.push({
        key,
        conditions: [{ value, operator: "=" }],
      });
    }

    updateBrandConfig({ filters: newFilters });
  };

  const loadMoreAds = async () => {
    if (!brandConfig || !selectedAccount?.id || !cursor || !hasNext || isLoadingMore) {
      return;
    }
    if (selectedBrands.length === 0) return;

    setIsLoadingMore(true);

    try {
      const brandIds = selectedBrands.map((brand) => brand.id);

      const response = await fetchMultiBrandAds({
        accountId: selectedAccount.id,
        brandIds,
        sortType: sortBy,
        creativeType: mediaType ?? undefined,
        adStatus: adStatus ?? undefined,
        location: location ?? undefined,
        pageSize: PAGE_SIZE,
        cursor,
      });

      if (!isMounted.current) return;

      // Append new ads to existing ads
      setAllAds((prevAds) => [...prevAds, ...response.ads]);
      setCursor(response.next_cursor);
      setHasNext(response.has_next);
      setStatuses(response.statuses || {});
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      console.error("Failed to fetch more brand ads:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      if (!isLoadingMore && hasNext && cursor) {
        void loadMoreAds();
      }
    }
  };

  // Clear polling timeout helper
  const clearLoadAdsPollingTimeout = useCallback(() => {
    if (loadAdsPollingTimeoutRef.current) {
      clearTimeout(loadAdsPollingTimeoutRef.current);
    }
    loadAdsPollingTimeoutRef.current = null;
  }, []);

  // Clear debounce timeout for loadAds
  const clearLoadAdsDebounceTimeout = useCallback(() => {
    if (loadAdsDebounceTimeoutRef.current) {
      clearTimeout(loadAdsDebounceTimeoutRef.current);
    }
    loadAdsDebounceTimeoutRef.current = null;
  }, []);

  // Clear polling timeout for loadBrands
  const clearLoadBrandsPollingTimeout = useCallback(() => {
    if (loadBrandsPollingTimeoutRef.current) {
      clearTimeout(loadBrandsPollingTimeoutRef.current);
    }
    loadBrandsPollingTimeoutRef.current = null;
  }, []);

  // Clear a specific pollBrandStatus timeout
  const clearPollBrandStatusTimeout = useCallback((brandId: string) => {
    const existingTimeout = pollBrandStatusTimeoutsRef.current.get(brandId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      pollBrandStatusTimeoutsRef.current.delete(brandId);
    }
  }, []);

  // Clear all pollBrandStatus timeouts
  const clearAllPollBrandStatusTimeouts = useCallback(() => {
    pollBrandStatusTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    pollBrandStatusTimeoutsRef.current.clear();
  }, []);

  const pollBrandStatus = async (brandId: string) => {
    // Clear any existing timeout for this brand before starting
    clearPollBrandStatusTimeout(brandId);

    setBrands(
      (prev) =>
        prev?.map((brand) =>
          brand.id === brandId ? { ...brand, sync_status: "pending" } : brand,
        ) || [],
    );
    setStatuses((prev) => ({ ...prev, [brandId]: "pending" }));
    try {
      const res = await getBrandStatus({ brandId });

      // Check if still mounted before updating state
      if (!isMounted.current) return;

      setBrands(
        (prev) => prev?.map((brand) => (brand.id === brandId ? res.brand : brand)) || [],
      );
      if (res.status === "success") {
        setStatuses((prev) => ({ ...prev, [brandId]: "success" }));
        clearPollBrandStatusTimeout(brandId);
        void loadAds();
      }
      if (res.status === "pending") {
        if (isMounted.current) {
          const timeoutId = setTimeout(() => {
            void pollBrandStatus(brandId);
          }, 5000);
          pollBrandStatusTimeoutsRef.current.set(brandId, timeoutId);
        }
      }
    } catch (error) {
      if (!isMounted.current) return;
      console.error("Failed to poll brand status:", error);
      clearPollBrandStatusTimeout(brandId);
      setBrands(
        (prev) =>
          prev?.map((brand) =>
            brand.id === brandId ? { ...brand, sync_status: "failed" } : brand,
          ) || [],
      );
      setStatuses((prev) => ({ ...prev, [brandId]: "failed" }));
    }
  };

  const handleSyncBrand = (brandId: string) => {
    if (!selectedAccount?.id) return;

    const brand = brands?.find((b) => b.id === brandId);
    if (!brand) return;

    if (brand.is_private) {
      // Use extension sync for private brands
      void syncBrandsWithExtension([brand], selectedAccount.id, true);
    } else {
      // Use polling for non-private brands
      void pollBrandStatus(brandId);
    }
  };

  const handleBrandAddedOptimistic = (optimisticBrand: AccountBrand) => {
    setBrands((prev) => [...(prev || []), optimisticBrand]);
    // Add optimistic brand id to selected state in brandConfig using latest state
    updateBrandConfig((prev) => ({
      ...prev,
      brand_ids: [...prev.brand_ids, optimisticBrand.id],
    }));
  };
  const handleBrandAddedSuccess = (
    optimisticBrandId: string,
    actualBrand: AccountBrand,
  ) => {
    setBrands(
      (prev) =>
        prev?.map((brand) => (brand.id === optimisticBrandId ? actualBrand : brand)) ||
        [],
    );

    // Update brand config using latest state to replace optimistic id with actual id
    updateBrandConfig((prev) => {
      if (!prev.brand_ids.includes(optimisticBrandId)) return prev;
      const filteredBrandIds = prev.brand_ids.filter((id) => id !== optimisticBrandId);
      return { ...prev, brand_ids: [...filteredBrandIds, actualBrand.id] };
    });

    // Only start polling if API succeeded and status is pending
    if (actualBrand.sync_status === "pending") {
      void loadBrands(true);
    }
  };

  const handleBrandAddedError = (optimisticBrandId: string) => {
    setBrands((prev) => prev?.filter((brand) => brand.id !== optimisticBrandId) || []);
  };

  const handleBrandRollback = (brand: AccountBrand) => {
    setBrands((prev) => {
      // Only add if not already present
      const exists = prev?.some((b) => b.id === brand.id);
      if (!exists) {
        return [...(prev || []), brand];
      }
      return prev || [];
    });

    // Restore to brandConfig selection since it was removed optimistically
    updateBrandConfig((prev) => {
      if (prev.brand_ids.includes(brand.id)) return prev;
      return { ...prev, brand_ids: [...prev.brand_ids, brand.id] };
    });
  };

  const loadBrandConfig = useCallback(
    async (brandsData: AccountBrand[] | null) => {
      if (!selectedAccount?.id) return;
      setBrandConfigLoading(true);
      try {
        const configData = await fetchBrandConfig(selectedAccount.id);
        // If config is empty, set defaults using the fetched brands
        if (!configData.config || Object.keys(configData.config).length === 0) {
          const defaultConfig: BrandConfig = {
            filters: [],
            brand_ids: brandsData?.map((brand) => brand.id) || [],
            sort_type: "newest",
            location: null,
          };
          setBrandConfig(defaultConfig);
          setBrands(brandsData); // order doesn't matters

          // Initialize selectedDate with default last 30 days
          const timezone = selectedAccount?.time_zone || "UTC";
          const predefinedRanges = getPredefinedRanges(timezone);
          const last30Days = predefinedRanges[TimeInterval.LAST_30_DAYS];
          if (last30Days) {
            setSelectedDate({
              start_date: formatForAPI(last30Days.from, timezone),
              end_date: formatForAPI(last30Days.to, timezone),
              label: TimeInterval.LAST_30_DAYS,
            });
          }
        } else {
          setBrandConfig(configData.config);
          // ordered when config is set
          setBrands(
            brandsData
              ? [
                  // First, brands whose ids are present in configData.config.brand_ids, in that order
                  ...(configData.config?.brand_ids
                    ? (configData.config.brand_ids
                        .map((id: string) => brandsData.find((b) => b.id === id))
                        .filter(Boolean) as AccountBrand[])
                    : []),
                  // Then, the rest of the brands not present in brand_ids, in their original order
                  ...brandsData.filter(
                    (b) => !(configData.config?.brand_ids || []).includes(b.id),
                  ),
                ]
              : brandsData,
          );

          // Initialize selectedDate from overview_config if available
          if (configData.config?.overview_config?.date) {
            setSelectedDate({
              start_date: configData.config.overview_config.date.start_date,
              end_date: configData.config.overview_config.date.end_date,
              label: configData.config.overview_config.date.label as TimeInterval,
            });
          } else {
            // Fallback to default last 30 days
            const timezone = selectedAccount?.time_zone || "UTC";
            const predefinedRanges = getPredefinedRanges(timezone);
            const last30Days = predefinedRanges[TimeInterval.LAST_30_DAYS];
            if (last30Days) {
              setSelectedDate({
                start_date: formatForAPI(last30Days.from, timezone),
                end_date: formatForAPI(last30Days.to, timezone),
                label: TimeInterval.LAST_30_DAYS,
              });
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch brands or brand config:", error);
        setBrandConfig({
          filters: [],
          brand_ids: [],
          sort_type: "newest",
          location: null,
        });
      } finally {
        setBrandConfigLoading(false);
      }
    },
    [selectedAccount?.id, selectedAccount?.time_zone],
  );

  const loadBrands = useCallback(
    async (isPolling?: boolean) => {
      if (!selectedAccount?.id) return;

      if (loadBrandsCancelTokenRef.current) loadBrandsCancelTokenRef.current.cancel();

      loadBrandsCancelTokenRef.current = axios.CancelToken.source();

      if (!isPolling) setBrandsLoading(true);
      try {
        const brandsData = await fetchAccountBrands({
          accountId: selectedAccount.id,
          cancelToken: loadBrandsCancelTokenRef.current.token,
        });

        // maintaining order
        setBrands((prev) => {
          if (!prev) return brandsData;
          // Create a map of new brands by id for quick lookup
          const newBrandsMap = new Map(brandsData.map((b) => [b.id, b]));
          // Update each previous brand with new data if available, preserving order
          return prev.map((brand) => newBrandsMap.get(brand.id) || brand);
        });

        // Set loading to false and load brand config on first call
        if (!isPolling) setBrandsLoading(false);
        if (!isPolling)
          await loadBrandConfig(brandsData); // setting brands inside this function to have selected brands in the start
        else
          setBrands((prev) => {
            if (!prev) return brandsData;
            // Create a map of new brands by id for quick lookup
            const newBrandsMap = new Map(brandsData.map((b) => [b.id, b]));
            // Update each previous brand with new data if available, preserving order
            return prev.map((brand) => newBrandsMap.get(brand.id) || brand);
          });
        // Reset retry counter on successful load
        loadBrandsRetryCountRef.current = 0;

        // Check for brands that need automatic sync (private brands not synced in 24+ hours)
        // Only trigger on initial load, not during polling
        if (!isPolling && selectedAccount?.id) {
          const brandsNeedingSync = brandsData.filter(
            (brand) => brand.ad_library_url && needsSync(brand),
          );

          if (brandsNeedingSync.length > 0) {
            console.debug(
              `Found ${brandsNeedingSync.length} private brand(s) that need sync (last synced > 24h ago)`,
            );
            void syncBrandsWithExtension(brandsNeedingSync, selectedAccount.id);
          }
        }

        // Check if any brands have pending sync status
        // Extract the boolean BEFORE creating the closure to avoid retaining brandsData
        const hasPendingBrands = brandsData.some(
          (brand) => brand.sync_status === "pending",
        );

        // clear any previously scheduled polling before setting a new one
        clearLoadBrandsPollingTimeout();
        if (hasPendingBrands && isMounted.current) {
          // If we have pending brands, start polling without loading state
          const timeoutId = setTimeout(() => {
            void loadBrands(true);
          }, 5000);
          loadBrandsPollingTimeoutRef.current = timeoutId;
        }

        // Help garbage collection by releasing references to response data
        // The data has been stored in state, we don't need these references anymore
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        console.error("Failed to fetch brands or brand config:", error);
        if (!isPolling) setBrandsLoading(false);

        // Increment retry counter
        loadBrandsRetryCountRef.current += 1;
        const currentRetryCount = loadBrandsRetryCountRef.current;

        // Check if we should continue polling or stop after max retries
        if (isPolling && currentRetryCount < MAX_RETRY_ATTEMPTS) {
          // Calculate exponential backoff delay with jitter
          const exponentialDelay = Math.min(
            BASE_RETRY_DELAY * Math.pow(2, currentRetryCount - 1),
            MAX_RETRY_DELAY,
          );
          const jitter = Math.random() * 1000; // Add up to 1 second of jitter
          const finalDelay = exponentialDelay + jitter;

          console.log(
            `Retrying brands fetch (attempt ${currentRetryCount}/${MAX_RETRY_ATTEMPTS}) in ${Math.round(finalDelay)}ms`,
          );
          // Continue polling after calculated delay
          clearLoadBrandsPollingTimeout();
          if (isMounted.current) {
            const timeoutId = setTimeout(() => {
              void loadBrands(true);
            }, finalDelay);
            loadBrandsPollingTimeoutRef.current = timeoutId;
          }
        } else if (isPolling && currentRetryCount >= MAX_RETRY_ATTEMPTS) {
          // Stop polling after max retries
          clearLoadBrandsPollingTimeout();
          console.warn(
            `Stopped polling brands after ${MAX_RETRY_ATTEMPTS} consecutive failures`,
          );
        }
        if (currentRetryCount >= MAX_RETRY_ATTEMPTS) {
          // Show toast when polling fails after max retries
          toast.message(
            <ToastContent
              icon={LuTriangleAlert}
              headingPrefix="Failed to sync brands"
              subheadingPrefix="Brand sync failed after multiple attempts. Please try refreshing the page."
              isError
            />,
          );
        }
      } finally {
        loadBrandsCancelTokenRef.current = null;
      }
    },
    [selectedAccount?.id, loadBrandConfig, clearLoadBrandsPollingTimeout],
  );

  const loadAds = async (shouldUpdateAds: boolean = true) => {
    if (!brandConfig) return;
    if (!selectedAccount?.id) return;

    if (loadAdsCancelTokenRef.current) loadAdsCancelTokenRef.current.cancel();

    if (selectedBrands.length === 0) {
      setAllAds([]);
      setStatuses({});
      setCursor(null);
      setHasNext(false);
      setTotalCount(0);
      setPreviousTotalCount(0);
      setNewAds([]);
      setNewAdsCursor(null);
      setNewAdsHasNext(false);
      return;
    }

    loadAdsCancelTokenRef.current = axios.CancelToken.source();

    try {
      const brandIds = selectedBrands.map((brand) => brand.id);
      const response = await fetchMultiBrandAds({
        accountId: selectedAccount.id,
        brandIds,
        sortType: sortBy,
        creativeType: mediaType ?? undefined,
        adStatus: adStatus ?? undefined,
        location: location ?? undefined,
        pageSize: PAGE_SIZE,
        cursor: null, // Always start fresh when loading ads
        isPolling: false, // always false to let ads load
        cancelToken: loadAdsCancelTokenRef.current.token,
      });
      if (!isMounted.current) return;

      setStatuses(response.statuses || {});

      const hasPendingBrands = Object.values(response.statuses || {}).some(
        (status) => status === "pending",
      );

      // set adsLoading false if no pending brands
      if (!hasPendingBrands) setAdsLoading(false);

      // ads update logic
      if (response.ads.length > 0) {
        if (shouldUpdateAds) {
          setAllAds(response.ads);
          setNewAds([]);
          setCursor(response.next_cursor);
          setHasNext(response.has_next);
          setPreviousTotalCount(response.total_ad_count);
        } else {
          // When not updating ads, compute new ads with isNew flags
          // Extract primitive values BEFORE any closures to avoid retaining the full response
          const responseAds = response.ads;
          const nextCursor = response.next_cursor;
          const hasNextPage = response.has_next;
          const totalAdCount = response.total_ad_count;

          // Use functional update only to read current state, compute flags synchronously
          setAllAds((currentAllAds) => {
            // Compute new ads with isNew flags based on current state
            const currentAdIds = new Set(currentAllAds.map((ad) => ad.id));
            const adsWithNewFlags = responseAds.map((ad) => ({
              ...ad,
              isNew: !currentAdIds.has(ad.id),
            }));

            // Schedule these updates for next tick to avoid nested setState
            // Using queueMicrotask for immediate scheduling without setTimeout overhead
            queueMicrotask(() => {
              if (isMounted.current) {
                setNewAds(adsWithNewFlags);
                setNewAdsCursor(nextCursor);
                setNewAdsHasNext(hasNextPage);
                setTotalCount(totalAdCount);
              }
            });

            // Return the current ads unchanged
            return currentAllAds;
          });
        }
        // set ads loading false if ads > 0
        setAdsLoading(false);
      }
      // polling
      if (hasPendingBrands) {
        clearLoadAdsPollingTimeout();
        // Extract the boolean BEFORE creating the closure to avoid retaining the full response object
        const shouldUpdateAdsOnNextPoll = response.ads.length < PAGE_SIZE;
        const timeoutId = setTimeout(() => {
          void loadAds(shouldUpdateAdsOnNextPoll); // to update initial ads in page view
        }, 5000);
        loadAdsPollingTimeoutRef.current = timeoutId;
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      console.error("Failed to fetch brand ads:", error);
      setAdsLoading(false);
      clearLoadAdsPollingTimeout();
    } finally {
      loadAdsCancelTokenRef.current = null;
    }
  };

  useEffect(() => {
    void loadBrands(false);
  }, [loadBrands]);

  // Load ads when brandConfig changes
  useEffect(() => {
    // debounce frequent changes
    clearLoadAdsDebounceTimeout();
    // set ads loading
    setAdsLoading(true);
    // Reset pagination states
    setAllAds([]);
    setCursor(null);
    setHasNext(false);
    setTotalCount(0);
    setPreviousTotalCount(0);
    setNewAdsCursor(null);
    setNewAdsHasNext(false);
    // Reset new ads
    setNewAds([]);
    isClickOnSyncedRef.current = false;

    // if any selected brand is optimistic don't call api just keep things in loading state
    if (selectedBrands.some((brand) => brand.id.includes("optimistic"))) return;
    // load ads
    loadAdsDebounceTimeoutRef.current = setTimeout(() => {
      void loadAds();
    }, 700);

    return () => {
      clearLoadAdsDebounceTimeout();
      clearLoadAdsPollingTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandConfig]);

  // Auto-switch away from impressions sort when multiple brands are selected
  useEffect(() => {
    if (selectedBrands.length > 1 && sortBy === "impressions") {
      updateBrandConfig({ sort_type: "running_days" });
      toast.custom((t) => (
        <ToastCompact
          isWarning
          messageHighlight="Multiple brands selected, sorting changed to Longest Running"
          onDismiss={() => toast.dismiss(t)}
        />
      ));
    }
     
  }, [selectedBrands.length, sortBy]);

  // Cleanup effect
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      clearLoadAdsPollingTimeout();
      clearLoadBrandsPollingTimeout();
      clearLoadAdsDebounceTimeout();
      clearAllPollBrandStatusTimeouts();

      // Cancel any pending API requests
      if (loadAdsCancelTokenRef.current) {
        loadAdsCancelTokenRef.current.cancel();
        loadAdsCancelTokenRef.current = null;
      }
      if (loadBrandsCancelTokenRef.current) {
        loadBrandsCancelTokenRef.current.cancel();
        loadBrandsCancelTokenRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup object URLs only when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining object URLs to prevent memory leaks
      creativeUploadingFiles.forEach((item) => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount/unmount

  // Listen for extension sync completion messages
  useEffect(() => {
    const handleExtensionMessage = (event: MessageEvent) => {
      // Only process messages from the extension
      const data = event.data as
        | {
            source?: string;
            type?: string;
            pageId?: string;
            adsCount?: number;
            error?: string;
          }
        | null
        | undefined;

      if (!data || data.source !== "getcrux-extension") return;

      const { pageId, type } = data;

      // Handle user logged out message
      if (type === "user_logged_out") {
        toast.custom((t) => (
          <ToastCompact
            isWarning
            messageHighlight="Log in to Meta to sync"
            hasDescription
            descriptionPrefix="Can’t sync private brands, you’re logged out of Meta."
            onDismiss={() => toast.dismiss(t)}
          />
        ));
        return;
      }

      if (type !== "external_brand_sync_first_batch_complete") return;

      if (!pageId) return;

      // Check if any selected brand matches the pageId
      const matchingBrand = selectedBrands.find((brand) => brand.page_id === pageId);

      if (matchingBrand) {
        console.debug(
          `Extension sync first batch complete for brand ${matchingBrand.name} (pageId: ${pageId}), loading ads...`,
        );
        void loadAds();
      }
    };

    window.addEventListener("message", handleExtensionMessage);

    return () => {
      window.removeEventListener("message", handleExtensionMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrands]);

  const handleBookmark = useCallback(
    async (adId: string, folderId: string) => {
      if (!selectedAccount?.id) return;

      const folder = folders.find((f) => f.id === folderId);
      if (!folder) return;

      // Use functional update to avoid capturing allAds in closure
      let previousAds: BrandAd[] = [];
      let isInFolder = false;

      setAllAds((prevAds) => {
        previousAds = prevAds;
        const adToUpdate = prevAds.find((ad) => ad.id === adId);
        isInFolder = adToUpdate?.folders.some((f) => f.id === folderId) ?? false;

        return prevAds.map((ad) =>
          ad.id === adId
            ? {
                ...ad,
                folders: isInFolder
                  ? ad.folders.filter((f) => f.id !== folderId)
                  : [...ad.folders, folder],
              }
            : ad,
        );
      });

      try {
        if (isInFolder) {
          await removeAdFromFolder({
            accountId: selectedAccount.id,
            folderId,
            adId,
          });
        } else {
          await saveAdToFolder({
            accountId: selectedAccount.id,
            folderId,
            adId,
            name: folder.name,
          });
        }
      } catch (error) {
        setAllAds(previousAds);
        console.error("Error handling bookmark:", error);
      }
    },
    [selectedAccount?.id, folders],
  );

  const handleCreateAd = useCallback(
    async (adId: string, settings: CreateAdSettings) => {
      if (!selectedAccount?.id) return;

      try {
        await generateAd({
          accountId: selectedAccount.id,
          adIds: [adId],
          defaultPromptId: settings.defaultPromptId,
          aspectRatio: settings.aspectRatio,
          numberOfMedia: settings.variations,
          model: settings.model,
          moodboardId: settings.moodboardId ?? selectedMoodboardId ?? null,
        });
        toast.custom((t) => (
          <ClickableToast
            icon={IconInfoCircle}
            iconProps={{
              size: 18,
              className: "text-[#2A94F4]",
            }}
            iconBackgroundClassName="bg-[#2A94F414]"
            hasDescription={true}
            messageHighlight="Ad Creation Started"
            descriptionPrefix="Switch to the Creatives tab to view the created ad."
            onClick={() => {
              if (selectedAccount?.id) {
                navigate(`${buildRoute.create(selectedAccount.id)}?type=image`);
                toast.dismiss(t);
              }
            }}
            onDismiss={() => toast.dismiss(t)}
          />
        ));
      } catch (error) {
        console.error("Failed to generate ad:", error);
      }
    },
    [selectedAccount?.id, navigate, selectedMoodboardId],
  );

  const hasPendingBrands = Object.values(statuses).some((status) => status === "pending");

  // Add new handler for brand chip toggles
  const handleBrandToggle = (brand: AccountBrand) => {
    if (!brandConfig) return;

    const isSelected = selectedBrands.some((selected) => selected.id === brand.id);
    let newBrandIds: string[];

    if (isSelected) {
      // Remove brand from selection
      newBrandIds = brandConfig.brand_ids.filter((id) => id !== brand.id);
    } else {
      // Add brand to selection
      newBrandIds = [...brandConfig.brand_ids, brand.id];
    }

    updateBrandConfig({ brand_ids: newBrandIds });
  };

  // Batch update: set multiple brand ids in one call (e.g. select/deselect all filtered)
  const handleBrandsSet = (newBrandIds: string[]) => {
    if (!brandConfig) return;
    updateBrandConfig({ brand_ids: newBrandIds });
  };

  const handleSelectAll = () => {
    if (!brands || !brandConfig) return;
    if (brandConfig.brand_ids.length === brands.length) {
      updateBrandConfig({ brand_ids: [] });
    } else {
      const allBrandIds = brands.map((brand) => brand.id);
      updateBrandConfig({ brand_ids: allBrandIds });
    }
  };

  // Ad selection handlers
  const totalAttachments =
    selectedAdIds.length + creativeFiles.length + creativeLibraryMedia.length;

  // Use refs to access current values in callback without causing re-renders
  const creativeFilesLengthRef = useRef(creativeFiles.length);
  const creativeLibraryMediaLengthRef = useRef(creativeLibraryMedia.length);
  creativeFilesLengthRef.current = creativeFiles.length;
  creativeLibraryMediaLengthRef.current = creativeLibraryMedia.length;

  const handleAdToggle = useCallback((adId: string) => {
    setSelectedAdIds((prev) => {
      const isSelected = prev.includes(adId);
      if (isSelected) {
        return prev.filter((id) => id !== adId);
      } else {
        // Compute total attachments using current values from refs and prev state
        const currentTotalAttachments =
          prev.length +
          creativeFilesLengthRef.current +
          creativeLibraryMediaLengthRef.current;

        // Check if we can add more
        if (currentTotalAttachments >= ATTACHMENT_LIMIT) {
          toast.message(
            <ToastContent
              icon={LuInfo}
              headingPrefix="Attachment limit reached"
              subheadingPrefix={`You can only attach up to ${ATTACHMENT_LIMIT} items in total.`}
              isError={false}
              isWarning={true}
            />,
          );
          return prev;
        }
        return [...prev, adId];
      }
    });
  }, []);

  const handleRemoveSelectedAd = (adId: string) => {
    setSelectedAdIds((prev) => prev.filter((id) => id !== adId));
  };

  const handleCreativeFileUpload = async (selectedFiles: globalThis.File[]) => {
    // Enforce overall attachment limit (includes files, library media, and selected ads)
    const capacity =
      ATTACHMENT_LIMIT -
      (selectedAdIds.length + creativeFiles.length + creativeLibraryMedia.length);

    if (selectedFiles.length > capacity) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix={
            capacity === 0 ? "Attachment limit reached" : "Cannot attach more files"
          }
          subheadingPrefix={`You can only attach up to ${ATTACHMENT_LIMIT} items in total.`}
          isError={false}
          isWarning={true}
        />,
      );
      return;
    }

    if (selectedFiles.length > 0) {
      // Create immediate preview items with temp IDs
      const newUploadingItems: UploadingFile[] = selectedFiles.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const preview = URL.createObjectURL(file);
        return { file, preview, id: tempId };
      });

      setCreativeUploadingFiles(newUploadingItems);
      setIsCreativeFileUploading(true);

      try {
        const filesToProcess = selectedFiles.slice(0, capacity);
        const response = await uploadFilesAsync(
          filesToProcess,
          selectedAccount?.id || "",
        );
        setCreativeFiles((prev) => {
          // Use a Map to deduplicate by id, always keeping the first occurrence
          const filesById = new Map<string, CustomFile>();
          [...prev, ...response.files].forEach((file) => {
            if (!filesById.has(file.id)) {
              filesById.set(file.id, file);
            }
          });
          return Array.from(filesById.values());
        });
        setCreativeUploadingFiles([]);
        // Clean up object URLs to prevent memory leaks after upload
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } catch (error) {
        console.error("Error uploading files:", error);
        // Clean up uploading files and object URLs on error
        setCreativeUploadingFiles([]);
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } finally {
        setIsCreativeFileUploading(false);
        if (creativeFileInputRef.current) {
          creativeFileInputRef.current.value = "";
        }
      }
    }
  };

  const handleCreativeFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files);
      void handleCreativeFileUpload(selectedFiles);
      // Close popover after file attachment
      setCreativePopoverOpen(false);
    }
  };

  const handleCreativeDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleCreativeDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCreativeDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCreativeDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files).filter((file) => {
      const fileType = file.type;
      return (
        fileType.startsWith("image/") ||
        fileType.startsWith("video/") ||
        fileType === "application/pdf"
      );
    });

    if (droppedFiles.length > 0) {
      await handleCreativeFileUpload(droppedFiles);
    }
  };

  const handleCreativePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageFiles = items
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);

    if (imageFiles.length > 0) {
      void handleCreativeFileUpload(imageFiles);
    }
  };

  // Wrapper functions to save to localStorage when settings change - memoized for stable references
  const handleCreativeAspectRatioChange = useCallback((newAspectRatio: AspectRatio) => {
    setCreativeAspectRatio(newAspectRatio);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO, newAspectRatio);
  }, []);

  const handleCreativeVariationsChange = useCallback((newVariations: Variations) => {
    setCreativeVariations(newVariations);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, newVariations);
  }, []);

  const handleCreativeModelChange = useCallback((newModel: Model) => {
    setCreativeModel(newModel);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.MODEL, newModel);
  }, []);

  const handleCreativeAvoidDangerZoneChange = useCallback(
    (newAvoidDangerZone: boolean) => {
      setCreativeAvoidDangerZone(newAvoidDangerZone);
      saveToLocalStorage(
        CREATIVE_LIBRARY_STORAGE_KEYS.AVOID_DANGER_ZONE,
        newAvoidDangerZone,
      );
    },
    [],
  );

  const handleCreativeDefaultPromptIdChange = useCallback((promptId: string | null) => {
    setCreativeDefaultPromptId(promptId);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.DEFAULT_PROMPT_ID, promptId);
  }, []);

  // Handler for settings changes from CreateAd - memoized for stable reference
  const handleCreateAdSettingsChange = useCallback(
    (settings: {
      model: Model;
      aspectRatio: AspectRatio;
      variations: Variations;
      defaultPromptId: string | null;
      moodboardId: string | null;
    }) => {
      handleCreativeModelChange(settings.model);
      handleCreativeAspectRatioChange(settings.aspectRatio);
      handleCreativeVariationsChange(settings.variations);
      handleCreativeDefaultPromptIdChange(settings.defaultPromptId);
      setSelectedMoodboardId(settings.moodboardId);
    },
    [
      handleCreativeModelChange,
      handleCreativeAspectRatioChange,
      handleCreativeVariationsChange,
      handleCreativeDefaultPromptIdChange,
      setSelectedMoodboardId,
    ],
  );

  const handleCreativeSubmit = async () => {
    if (!selectedAccount?.id || selectedAdIds.length === 0) return;

    // Store values before optimistic update
    const adIdsToSubmit = [...selectedAdIds];
    const promptText = creativePrompt.trim();
    const defaultPromptIdToUse = promptText ? null : creativeDefaultPromptId;
    const aspectRatioToUse = creativeAspectRatio;
    const variationsToUse = creativeVariations;
    const modelToUse = creativeModel;
    const avoidDangerZoneToUse = creativeAvoidDangerZone;
    const filesToUse = [...creativeFiles];
    const libraryMediaToUse = [...creativeLibraryMedia];
    const moodboardIdToUse = selectedMoodboardId;

    // Optimistically close input section and reset form
    setSelectedAdIds([]);
    setCreativePrompt("");
    setCreativeFiles([]);
    setCreativeLibraryMedia([]);
    setIsCreativeSubmitting(true);

    // Process API in background
    try {
      await generateAd({
        accountId: selectedAccount.id,
        adIds: adIdsToSubmit,
        defaultPromptId: defaultPromptIdToUse,
        prompt: promptText || null,
        aspectRatio: aspectRatioToUse,
        numberOfMedia: variationsToUse,
        model: modelToUse,
        avoidDangerZone: avoidDangerZoneToUse,
        fileIds: filesToUse.map((f) => f.id),
        libraryFileIds: libraryMediaToUse.map((m) => m.file_id),
        moodboardId: moodboardIdToUse,
      });

      toast.custom((t) => (
        <ClickableToast
          icon={IconInfoCircle}
          iconProps={{
            size: 18,
            className: "text-[#2A94F4]",
          }}
          iconBackgroundClassName="bg-[#2A94F414]"
          hasDescription={true}
          messageHighlight="Ad Creation Started"
          descriptionPrefix="Switch to the Creatives tab to view the created ad."
          onClick={() => {
            if (selectedAccount?.id) {
              navigate(`${buildRoute.create(selectedAccount.id)}?type=image`);
              toast.dismiss(t);
            }
          }}
          onDismiss={() => toast.dismiss(t)}
        />
      ));
    } catch (error) {
      console.error("Failed to generate ad:", error);
      toast.message(
        <ToastContent
          icon={LuTriangleAlert}
          headingPrefix="Failed to create ad"
          subheadingPrefix="Something went wrong. Please try again."
          isError
        />,
      );
    } finally {
      setIsCreativeSubmitting(false);
    }
  };

  // Get selected ads data for preview
  const selectedAdsData = useMemo(() => {
    return allAds.filter((ad) => selectedAdIds.includes(ad.id));
  }, [allAds, selectedAdIds]);

  const handleDateChange = useCallback((newDate: ReportDate | null) => {
    setSelectedDate(newDate);
  }, []);

  // Check if any filters are applied
  const hasFiltersApplied = useMemo(() => {
    if (!brandConfig) return false;
    // Check if media type filter is applied
    if (mediaType !== null) return true;
    // Check if ad status filter is applied
    if (adStatus !== null) return true;
    // Check if location filter is applied
    if (location !== null) return true;
    // Check if there are any other filters
    if (filters.length > 0) return true;
    return false;
  }, [brandConfig, mediaType, adStatus, location, filters]);

  // Reset all filters
  const handleResetFilters = useCallback(() => {
    setBrandConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        filters: [],
        location: null,
      };
    });
  }, []);

  if (brandsLoading || brandConfigLoading) {
    return (
      <div className="flex flex-col w-full h-full items-center justify-center">
        <PageLoader />
      </div>
    );
  }

  const handleAddDemoAccountClick = async (brandId: string) => {
    const toastId = toast.loading("Creating demo account...");
    try {
      const demoAccount = await addDemoAccount({ brandId });
      const accountRoute = buildRoute.account(demoAccount.id);
      const currentDomain = window.location.origin;
      window.open(`${currentDomain}${accountRoute}`, "_blank");
    } catch (error) {
      console.error(error);
    } finally {
      toast.dismiss(toastId);
    }
  };
  if (brands && brands.length === 0) {
    return (
      <div className="flex flex-col w-full h-full items-center justify-center">
        <NoCompetitors onAddBrand={() => setIsAddCompetitorDialogOpen(true)} />
        <AddCompetitorDialog
          isOpen={isAddCompetitorDialogOpen}
          onOpenChange={(open) => setIsAddCompetitorDialogOpen(open)}
          onBrandAddedOptimistic={handleBrandAddedOptimistic}
          onBrandAddedSuccess={handleBrandAddedSuccess}
          onBrandAddedError={handleBrandAddedError}
          accountId={selectedAccount?.id}
        />
      </div>
    );
  }
  return (
    <div className="relative flex flex-col w-full h-full pt-[8px] bg-background-gray">
      <div className={`ai-glow-wrapper ai-glow--input pointer-events-none absolute bottom-0 left-0 right-0 z-40 h-[80px] ${isInputFocused ? "ai-glow-active" : ""}`}>
        <div className="ai-glow-edge ai-glow-edge-bottom" />
      </div>
      <div className="flex justify-between items-center pl-[9px] pr-[14px] mb-[25px]">
        <div className="w-full flex gap-2 ">
          {sidebarCollapsed && <div className="h-[32px] w-[32px] shrink-0"></div>}
          {/* Sort Dropdown */}
          <SortByDropdown
            selectedType={sortBy}
            onChange={(type) => updateBrandConfig({ sort_type: type })}
            isImpressionSortDisabled={isImpressionSortDisabled}
          />

          <MediaTypeDropdown
            selectedType={mediaType}
            onChange={(type) => updateFilters("creative.creative_type", type)}
            className="h-8 rounded-[8px] border-0 squircle squircle-smooth-[1] squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white shadow-sm hover:bg-hover-gray"
          />
          <AdStatusDropdown
            selectedType={adStatus}
            onChange={(type) => updateFilters("status", type)}
            className="h-8 rounded-[8px] border-0 squircle squircle-smooth-[1] squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white shadow-sm hover:bg-hover-gray"
          />
          <LocationDropdown
            selectedLocation={location}
            onChange={(location) => updateBrandConfig({ location: location })}
            className="h-8 rounded-[8px] border-0 squircle squircle-smooth-[1] squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white shadow-sm hover:bg-hover-gray"
          />
        </div>
        <div>
          <CompetitorsDropdown
            brands={brands || []}
            onSyncBrand={handleSyncBrand}
            onBrandRemove={(brandId) => {
              setBrands((prev) => prev?.filter((brand) => brand.id !== brandId) || []);
              if (brandConfig && brandConfig.brand_ids.includes(brandId)) {
                updateBrandConfig({
                  brand_ids: brandConfig.brand_ids.filter((id) => id !== brandId),
                });
              }
            }}
            onBrandRollback={handleBrandRollback}
            loading={brandsLoading}
            onAddCompetitorClick={() => setIsAddCompetitorDialogOpen(true)}
            onAddDemoAccountClick={handleAddDemoAccountClick}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="w-full flex justify-between pl-[9px] pr-[14px]">
          <ReportTitle
            title={selectedSidebarItem?.label ?? ""}
            icon={
              selectedSidebarItem && (
                <selectedSidebarItem.icon size={16} className="text-brand-orange" />
              )
            }
            iconBackgroundColor="#FF45000F"
          />
        </div>

        {/* Brand Filter Chips */}
        <div className="mt-[12px] pl-[9px] pr-[14px]">
          {brands && brands.length > 0 && (
            <BrandFilterChips
              brands={brands}
              selectedBrands={selectedBrands}
              onBrandToggle={handleBrandToggle}
              onBrandsSet={handleBrandsSet}
              onSelectAll={handleSelectAll}
              onAddCompetitorClick={() => setIsAddCompetitorDialogOpen(true)}
            />
          )}
        </div>

        <Separator className="w-full mt-[19px]" color="#67749B29" />

        <div
          ref={scrollContainerRef}
          className={`w-full flex-1 px-[9px] pt-[12px] overflow-y-auto overflow-x-hidden relative ${
            selectedAdIds.length > 0 ? "pb-[200px]" : "pb-[30px]"
          }`}
          onScroll={handleScroll}
        >
          {brands && brands.length === 0 ? (
            <NoCompetitors onAddBrand={() => setIsAddCompetitorDialogOpen(true)} />
          ) : selectedBrands.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <p className="text-gray-500 mb-4">Select competitors to view their ads</p>
              </div>
            </div>
          ) : (adsLoading || hasPendingBrands) && allAds.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center">
              <PageLoader />
            </div>
          ) : !hasPendingBrands && allAds.length === 0 ? (
            <NoAds
              hasFiltersApplied={hasFiltersApplied}
              onResetFilters={handleResetFilters}
              onAddBrand={() => setIsAddCompetitorDialogOpen(true)}
            />
          ) : (
            <>
              {newAds.length > 0 && totalCount > previousTotalCount ? (
                <div className="sticky top-0 z-30 h-0 pointer-events-none">
                  <div
                    className="absolute top-4"
                    style={{
                      left: `${gridWidth / 2}px`,
                      transform: "translateX(-50%)",
                    }}
                  >
                    <Button
                      variant="outline"
                      onClick={() => {
                        setAllAds([...newAds]);
                        setNewAds([]);
                        setCursor(newAdsCursor);
                        setHasNext(newAdsHasNext);
                        setPreviousTotalCount(totalCount);
                        setNewAdsCursor(null);
                        setNewAdsHasNext(false);
                        isClickOnSyncedRef.current = true;
                        scrollContainerRef.current?.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }}
                      className="pointer-events-auto flex w-[130px] items-center gap-1 text-white rounded-[8px] bg-[rgba(0,0,0,0.80)] hover:bg-[rgba(0,0,0,0.90)] shadow-[0_2px_25px_19px_rgba(255,255,255,0.60)] backdrop-blur-[2px] border-0"
                    >
                      <IconRefresh
                        size={16}
                        className="text-[#FFFFFF99] shrink-0"
                        strokeWidth={1}
                      />
                      <div className="relative inline-block w-full h-[19px] overflow-hidden top-[1px]">
                        <span className="text-[12px] text-white font-normal leading-[19.157px] tracking-[0.24px] absolute inset-0 text-swap-1">
                          {totalCount - previousTotalCount} New ad
                          {totalCount - previousTotalCount > 1 ? "s" : ""}
                        </span>
                        <span className="text-[12px] text-white font-normal leading-[19.157px] tracking-[0.24px] absolute inset-0 text-swap-2">
                          Click to Sync
                        </span>
                      </div>
                    </Button>
                  </div>
                </div>
              ) : null}
              {allAds.length > 0 && (
                <div className="mb-[11px]">
                  <CompetitorAdsOverview
                    selectedBrandIds={selectedBrands.map((brand) => brand.id)}
                    filters={filters}
                    location={location}
                    brands={selectedBrands?.map((brand) => ({
                      id: brand.id,
                      name: brand.name,
                      icon_url: brand.profile_image_url || undefined,
                    }))}
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                    refetchToken={previousTotalCount}
                  />
                </div>
              )}
              <MasonryLayout
                items={allAds}
                onGridWidthChange={setGridWidth}
                renderItem={(ad: BrandAd) => {
                  const adRank =
                    sortBy === "impressions"
                      ? allAds.indexOf(ad) + 1
                      : null;
                  return (
                  <CompetitorAdCard
                    key={ad.id}
                    ad={ad}
                    isSelected={selectedAdIds.includes(ad.id)}
                    totalAttachments={totalAttachments}
                    attachmentLimit={ATTACHMENT_LIMIT}
                    folders={folders}
                    rank={adRank}
                    onAdToggle={handleAdToggle}
                    onBookmark={handleBookmark}
                    onCreateAd={handleCreateAd}
                    creativeModel={creativeModel}
                    creativeAspectRatio={creativeAspectRatio}
                    creativeVariations={creativeVariations}
                    creativeDefaultPromptId={creativeDefaultPromptId}
                    moodboards={moodboards}
                    moodboardsLoading={moodboardsLoading}
                    selectedMoodboardId={selectedMoodboardId}
                    onCreateMoodboard={() => void handleQuickCreateMoodboard()}
                    onEditMoodboard={(id) => openMoodboardDialog(id)}
                    onSettingsChange={handleCreateAdSettingsChange}
                  />
                  );
                }}
                cardWidth={296}
                cardGap={12}
                maxColumns={5}
              />
              {isLoadingMore && (
                <div className="w-full flex justify-center mt-4">
                  <ItemLoader />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Creative Input Section - shown when ads are selected */}
      {selectedAdIds.length > 0 && (
        <div className="absolute bottom-0 left-1/2 translate-x-[-50%] z-50 w-full">
          <div className="max-w-[750px] mx-auto px-4 py-3">
            <CreativeInputSection
              selectedAds={selectedAdsData.map((ad) => ({
                id: ad.id,
                thumbnailUrl:
                  ad.creative.images?.[0]?.permalink_url || ad.creative.thumbnail_url,
                accountName: ad.creative.account_name,
                accountIconUrl: ad.creative.account_icon_url,
              }))}
              onRemoveSelectedAd={handleRemoveSelectedAd}
              prompt={creativePrompt}
              creativeType="image"
              aspectRatio={creativeAspectRatio}
              variations={creativeVariations}
              avoidDangerZone={creativeAvoidDangerZone}
              model={creativeModel}
              files={creativeFiles}
              libraryMedia={creativeLibraryMedia}
              uploadingFiles={creativeUploadingFiles}
              isFileUploading={isCreativeFileUploading}
              submitting={isCreativeSubmitting}
              popoverOpen={creativePopoverOpen}
              moodboards={moodboards}
              moodboardId={selectedMoodboardId}
              moodboardsLoading={moodboardsLoading}
              onPromptChange={setCreativePrompt}
              onCreativeTypeChange={() => {}}
              showCreativeTypeDropdown={false}
              onAspectRatioChange={handleCreativeAspectRatioChange}
              onVariationsChange={handleCreativeVariationsChange}
              onAvoidDangerZoneChange={handleCreativeAvoidDangerZoneChange}
              onModelChange={handleCreativeModelChange}
              onMoodboardChange={setSelectedMoodboardId}
              onCreateMoodboard={() => void handleQuickCreateMoodboard()}
              onEditMoodboard={(id) => openMoodboardDialog(id)}
              onFileChange={handleCreativeFileChange}
              onLibraryAttachmentChange={setCreativeLibraryMedia}
              onRemoveFile={(file) =>
                setCreativeFiles((prev) => prev.filter((f) => f.id !== file.id))
              }
              onRemoveLibraryAttachment={(file) =>
                setCreativeLibraryMedia((prev) =>
                  prev.filter((m) => m.file_id !== file.id),
                )
              }
              onPaste={handleCreativePaste}
              onDragOver={handleCreativeDragOver}
              onDragEnter={handleCreativeDragEnter}
              onDragLeave={handleCreativeDragLeave}
              onDrop={handleCreativeDrop}
              onSubmit={handleCreativeSubmit}
              onPopoverOpenChange={setCreativePopoverOpen}
              fileInputRef={creativeFileInputRef}
              creativeTypeDisabled
              containerClassName="!p-3 !pt-2"
              onFocusChange={setIsInputFocused}
            />
          </div>
        </div>
      )}

      {selectedAccount?.id && (
        <MoodboardManagerDialog
          open={moodboardDialogOpen}
          onOpenChange={setMoodboardDialogOpen}
          accountId={selectedAccount.id}
          moodboards={moodboards}
          selectedMoodboardId={selectedMoodboardId}
          initialActiveMoodboardId={moodboardDialogInitialActiveId}
          onSelectMoodboardId={setSelectedMoodboardId}
          onMoodboardsChange={onMoodboardsChange}
        />
      )}

      <AddCompetitorDialog
        isOpen={isAddCompetitorDialogOpen}
        onOpenChange={(open) => setIsAddCompetitorDialogOpen(open)}
        onBrandAddedOptimistic={handleBrandAddedOptimistic}
        onBrandAddedSuccess={handleBrandAddedSuccess}
        onBrandAddedError={handleBrandAddedError}
        accountId={selectedAccount?.id}
        addedBrands={brands || []}
      />
    </div>
  );
};

export default CompetitorAdsAnalysis;
